/**
 * Comparación visual: anuncio Idealista (original) vs datos importados al modelo canónico (portal v2).
 * Idealista suele bloquear iframe; se abre en nueva ventana o popup.
 */
import { useState, useCallback, useEffect, useRef } from "react";
import { useTheme } from "../components/ThemeProvider";
import api from "../services/api";
import { portalImportIdealistaAsync, mensajeErrorPortalImport, IDEALISTA_IMPORT_TIMEOUT_MS } from "../services/portalImportApi";
import { claveFotoIdealistaUrl } from "../utils/idealistaFotoKey";
import usePermiso from "../hooks/usePermiso";

const EJEMPLO_URL = "https://www.idealista.com/inmueble/105338480/";

/** Debe coincidir con CANONICAL_KEYS en backend `portal-idealista-v2.service.js` (garantiza filas aunque el JSON venga incompleto). */
const IDEALISTA_V2_CANONICAL_KEYS = [
  "titulo",
  "web_titulo",
  "operacion",
  "tipo_inmueble_id",
  "tipo_inmueble_nombre",
  "precio_venta",
  "web_desc_larga_es",
  "num_dormitorios",
  "num_banos",
  "num_terrazas",
  "num_balcones",
  "m2_construidos",
  "m2_utiles",
  "m2_parcela",
  "m2_terrazas",
  "m2_jardin",
  "m2_jardin_obs",
  "codigo_postal",
  "nombre_via",
  "numero",
  "poblacion_id",
  "poblacion_nombre",
  "referencia",
  "alias",
  "cal_energetica",
  "cal_energetica_consumo",
  "cal_energetica_emisiones",
  "orientacion_id",
  "orientacion_nombre",
  "plantas_inmueble",
  "altura_planta",
  "piso",
  "fecha_alta",
  "fecha_actualizacion",
  "ano_construccion",
  "estado_inmueble_id",
  "estado_inmueble_nombre",
  "tipo_anunciante",
  "telefono",
  "armarios",
  "planos",
  "eq_piscina_priv",
  "eq_piscina_comun",
  "eq_garaje",
  "eq_calefaccion",
  "eq_aire",
  "eq_ascensor",
  "eq_trastero",
  "eq_barbacoa",
  "eq_lavadero",
  "eq_chimenea",
  "eq_padel",
  "eq_gimnasio",
  "eq_tenis",
  "eq_agua",
  "eq_gas",
  "eq_luz",
  "ub_primera_linea",
  "ub_segunda_linea",
  "ub_vistas_id",
  "ub_distancia_mar",
  "amueblado",
  "exterior",
  "gastos_comunidad",
];

function normalizarTelefonoEs(raw) {
  if (raw == null || raw === "") return null;
  const d = String(raw)
    .replace(/\D/g, "")
    .replace(/^34/, "");
  return d.length >= 9 ? d.slice(-9) : null;
}

const IMG_PROXY_BASE =
  window.location.hostname === "localhost"
    ? "http://localhost:4000/api/img-proxy?url="
    : "https://vitalcasa-crm-production.up.railway.app/api/img-proxy?url=";

function proxyFoto(u) {
  if (!u || typeof u !== "string") return u;
  if (u.startsWith("data:")) return u;
  return IMG_PROXY_BASE + encodeURIComponent(u);
}

function fmtValor(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Sí" : "No";
  if (typeof v === "object") return JSON.stringify(v, null, 0);
  return String(v);
}

function fmtValorSnapshotCampo(k, snap, extras, snapReferencia, telefonoResuelto) {
  if (k === "referencia") return fmtValor(snapReferencia);
  if (k === "telefono") return fmtValor(telefonoResuelto);
  if (k === "eq_calefaccion" && snap.eq_calefaccion === true) {
    const parts = [extras.calefaccion_tipo_portal, extras.calefaccion_energia_portal].filter(Boolean);
    if (parts.length) return `Sí (${parts.join(" · ")})`;
  }
  return fmtValor(snap[k]);
}

/** Campos en portal_extras sin columna CRM directa (se muestran en la tabla). */
const IDEALISTA_PORTAL_EXTRAS_ROWS = [
  "estado_inmueble_portal",
  "estado_inmueble_nombre",
  "distancia_puerto_m",
  "distancia_centro_m",
  "distancia_playa_m",
  "video_youtube_url",
  "clasificacion_portal",
  "vistas_portal_label",
  "calefaccion_tipo_portal",
  "calefaccion_energia_portal",
  "accesibilidad_portal",
  "parque_infantil_portal",
  "construccion_auxiliar_portal",
  "tipo_cocina_portal",
  "piscina_acceso_directo_portal",
  "bajada_precio_texto",
  "precio_anterior_portal",
  "orientacion_portal",
  "orientaciones_portal",
  "parcela_m2_candidatos",
  "m2_construidos_candidatos",
  "agente_nombre_portal",
  "agencia_nombre_portal",
  "planos_urls",
  "tags_portal",
];

function fmtPrecio(n) {
  if (n == null || n === "") return "—";
  const x = Number(n);
  if (!Number.isFinite(x)) return String(n);
  return new Intl.NumberFormat("es-ES").format(x) + " €";
}

function fmtFechaHora(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function CompararPortalIdealistaV2() {
  const { theme } = useTheme();
  const t = theme.colors;
  const isDark = theme.name === "dark";
  const tx = t.textPrimary;
  const surface = t.bgCard;
  const { puede } = usePermiso();
  const verPagina = puede("cmp_idealista", "ver");
  const verTelManual = puede("cmp_idealista_tel_manual", "ver");
  const modTelManual = puede("cmp_idealista_tel_manual", "modificar");
  const [url, setUrl] = useState(EJEMPLO_URL);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [data, setData] = useState(null);
  const [telefonoManual, setTelefonoManual] = useState("");
  const [guardandoTelefono, setGuardandoTelefono] = useState(false);
  const [extensionTel, setExtensionTel] = useState(null);
  const extensionFetchRef = useRef(null);

  const pedirTelefonoExtension = useCallback((adId, listingUrl) => {
    const id = String(adId || "").replace(/\D/g, "");
    const u = String(listingUrl || "").trim();
    if (!id || !u) return;
    extensionFetchRef.current = id;
    setExtensionTel("buscando");
    window.postMessage({ type: "VC_IDEALISTA_FETCH_PHONE", adId: id, url: u }, "*");
  }, []);

  useEffect(() => {
    const onExtensionResult = async (e) => {
      if (e.source !== window || e.data?.type !== "VC_IDEALISTA_FETCH_PHONE_RESULT") return;
      const adId = String(e.data.adId || "").replace(/\D/g, "");
      if (!adId || extensionFetchRef.current !== adId) return;
      extensionFetchRef.current = null;
      if (e.data.ok && e.data.telefono) {
        setExtensionTel("ok");
        const u = url.trim();
        if (!u) return;
        try {
          const res = await portalImportIdealistaAsync({ url: u }, { timeoutMs: IDEALISTA_IMPORT_TIMEOUT_MS });
          setData(res);
        } catch {
          setExtensionTel("reimport_fallo");
        }
      } else {
        setExtensionTel(e.data.error === "extension_sin_configurar" ? "sin_extension" : (e.data.error || "error"));
      }
    };
    window.addEventListener("message", onExtensionResult);
    return () => window.removeEventListener("message", onExtensionResult);
  }, [url]);

  const ejecutar = useCallback(async () => {
    setErr(null);
    setData(null);
    const u = url.trim();
    if (!u || !/idealista\.com/i.test(u)) {
      setErr("Pega una URL completa de una ficha de idealista.com");
      return;
    }
    setLoading(true);
    try {
      const res = await portalImportIdealistaAsync({ url: u }, { timeoutMs: IDEALISTA_IMPORT_TIMEOUT_MS });
      const snap = res?.snapshot || {};
      const vacio = !snap.titulo && !snap.web_titulo && !snap.num_dormitorios && !(res?.fotos_urls?.length);
      if (vacio) {
        setErr(
          res?.import_meta?.htmlValid === false
            ? `Importación sin datos (HTML bloqueado, ${res.import_meta.htmlLen || 0} bytes). Reintenta en unos segundos.`
            : "Importación completada pero sin datos de la ficha. Reintenta la importación."
        );
        return;
      }
      setData(res);
      const snapTel = res?.snapshot?.telefono ?? res?.telefono;
      const adId = res?.id_anuncio ?? u.match(/\/inmueble\/(\d{6,12})/)?.[1];
      if (!snapTel && adId) {
        pedirTelefonoExtension(adId, u);
      } else {
        setExtensionTel(null);
      }
    } catch (e) {
      setErr(mensajeErrorPortalImport(e));
    } finally {
      setLoading(false);
    }
  }, [url, pedirTelefonoExtension]);

  const idAnuncioActual =
    data?.id_anuncio ??
    (String(data?.url_origen || url).match(/\/inmueble\/(\d{6,12})/)?.[1] ?? null);

  const guardarTelefonoManual = async () => {
    const adId = idAnuncioActual;
    const tel = telefonoManual.trim();
    if (!adId || !tel) return;
    setGuardandoTelefono(true);
    setErr(null);
    try {
      const { data: saved } = await api.post("/scraping/idealista-telefono-cache", { adId, telefono: tel });
      const norm = normalizarTelefonoEs(saved?.telefono ?? tel);
      if (!norm) {
        setErr("Teléfono no válido (necesita al menos 9 dígitos).");
        return;
      }
      setTelefonoManual("");
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          telefono: norm,
          snapshot: { ...(prev.snapshot || {}), telefono: norm },
          portal_extras: {
            ...(prev.portal_extras || {}),
            telefono_anuncio: norm,
            telefono_aviso: null,
          },
          provenance: {
            ...(prev.provenance || {}),
            telefono: "idealista.telefono_cache.manual",
          },
        };
      });
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setGuardandoTelefono(false);
    }
  };

  const abrirOriginalNuevaPestaña = () => {
    const u = (data?.url_origen || url).trim();
    if (u) window.open(u, "_blank", "noopener,noreferrer");
  };

  const abrirOriginalPopup = () => {
    const u = (data?.url_origen || url).trim();
    if (!u) return;
    const w = Math.min(980, window.screen.width - 80);
    const h = Math.min(1100, window.screen.height - 80);
    const left = window.screenX + window.outerWidth - w - 24;
    const top = window.screenY + 40;
    window.open(
      u,
      "idealista_comparar",
      `noopener,noreferrer,width=${w},height=${h},left=${left},top=${top},scrollbars=yes`
    );
  };

  const snap = data?.snapshot || {};
  const extras = data?.portal_extras || {};
  /** Misma columna CRM `propiedades.referencia`; snapshots antiguos pueden traer `referencia_portal`. */
  const snapReferencia = snap.referencia ?? snap.referencia_portal;
  const telefonoResuelto =
    snap.telefono ??
    data?.telefono ??
    normalizarTelefonoEs(extras.telefono_anuncio) ??
    normalizarTelefonoEs(data?.telefono_raw) ??
    null;
  /** Incluye `referencia` y `telefono` para que siempre salgan filas en la tabla (valor → "—" si null). */
  const snapKeys = [...new Set([...IDEALISTA_V2_CANONICAL_KEYS, ...Object.keys(snap), "referencia", "telefono"])].sort();
  const prov = data?.provenance || {};
  const provFuente = (k) => {
    if (k === "telefono") {
      const direct = prov.telefono;
      if (!direct) return telefonoResuelto ? "idealista.import_v2 (normalizado)" : null;
      if (String(direct).startsWith("sin_valor:")) return "Sin número en HTML (ScraperAPI / credenciales Idealista).";
      return direct;
    }
    const direct = prov[k] || (k === "referencia" ? prov.referencia_portal : null);
    if (direct) return direct;
    if (k === "referencia" && snapReferencia) return "idealista.import_v2 (referencia comercial)";
    return null;
  };
  const fotosRaw = Array.isArray(data?.fotos_urls) ? data.fotos_urls : [];
  const fotos = (() => {
    const keys = new Set();
    const out = [];
    for (const u of fotosRaw) {
      if (!u || typeof u !== "string") continue;
      const k = claveFotoIdealistaUrl(u);
      if (!k || keys.has(k)) continue;
      keys.add(k);
      out.push(u);
    }
    return out;
  })();
  const videosRaw = Array.isArray(data?.videos_urls) ? data.videos_urls : [];
  const videos = (() => {
    const keys = new Set();
    const out = [];
    for (const u of videosRaw) {
      if (!u || typeof u !== "string") continue;
      const k = u.split("?")[0].toLowerCase();
      if (!k || keys.has(k)) continue;
      keys.add(k);
      out.push(u);
    }
    return out;
  })();
  const planosRaw = Array.isArray(extras.planos_urls) ? extras.planos_urls : [];
  const youtubeThumb = (url) => {
    const m = String(url || "").match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{8,15})/i);
    return m ? `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg` : null;
  };
  const caraPortal = extras.caracteristicas_portal || {};
  const descLen = snap.web_desc_larga_es ? String(snap.web_desc_larga_es).length : 0;

  const card = {
    border: `1px solid ${t.border}`,
    borderRadius: 12,
    background: surface,
    padding: 16,
    marginBottom: 16,
  };

  if (!verPagina) {
    return (
      <div style={{ padding: 24, color: t.textMuted, fontSize: 14 }}>
        No tienes permiso para ver Comparar Idealista v2.
      </div>
    );
  }

  return (
    <div style={{ padding: "20px 24px 48px", maxWidth: 1280, margin: "0 auto" }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: tx, margin: "0 0 8px" }}>
        Comparar Idealista.com → import v2 (canónico)
      </h1>
      <p style={{ fontSize: 13, color: t.textMuted, margin: "0 0 16px", lineHeight: 1.5, maxWidth: 920 }}>
        El campo del snapshot se llama <strong style={{ color: tx }}>referencia</strong>, igual que en{" "}
        <code>propiedades.referencia</code>: aquí rellenamos la <strong style={{ color: tx }}>referencia comercial del anuncio</strong>{" "}
        que publica la agencia en Idealista (p. ej. 3132-C), si la encontramos en la ficha o en la descripción. La referencia
        automática tipo 10001-A del CRM, si la usas, se sigue generando al crear la propiedad; no viene del portal.
      </p>
      <p style={{ color: t.textMuted, fontSize: 14, margin: "0 0 20px", lineHeight: 1.5 }}>
        Importa una sola URL con el mismo sistema que Pisos v2 y revisa campo a campo. La ficha en
        idealista.com no se incrusta aquí; usa el botón para abrirla al lado de esta pantalla.
      </p>

      <div style={{ ...card, display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
        <label style={{ flex: "1 1 280px", display: "block" }}>
          <span style={{ display: "block", fontSize: 12, fontWeight: 600, color: t.textMuted, marginBottom: 6 }}>
            URL del anuncio (idealista.com)
          </span>
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.idealista.com/inmueble/..."
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 8,
              border: `1px solid ${t.border}`,
              background: isDark ? "rgba(0,0,0,0.25)" : "#fff",
              color: tx,
              fontSize: 14,
            }}
          />
        </label>
        <button
          type="button"
          onClick={ejecutar}
          disabled={loading}
          style={{
            padding: "10px 20px",
            borderRadius: 8,
            border: "none",
            fontWeight: 700,
            cursor: loading ? "wait" : "pointer",
            background: "linear-gradient(135deg, #0ea5e9, #6366f1)",
            color: "#fff",
            opacity: loading ? 0.75 : 1,
          }}
        >
          {loading ? "Importando…" : "Importar y mostrar"}
        </button>
        <button
          type="button"
          onClick={() => setUrl(EJEMPLO_URL)}
          style={{
            padding: "10px 14px",
            borderRadius: 8,
            border: `1px solid ${t.border}`,
            background: "transparent",
            color: t.textMuted,
            cursor: "pointer",
            fontSize: 13,
          }}
        >
          URL ejemplo
        </button>
      </div>
      {loading && (
        <p style={{ margin: "8px 0 0", fontSize: 13, color: t.textMuted }}>
          Idealista puede tardar 1–3 min (ScraperAPI). No cierres esta pestaña.
        </p>
      )}

      {err && (
        <div
          style={{
            ...card,
            borderColor: "rgba(239,68,68,0.5)",
            background: isDark ? "rgba(239,68,68,0.12)" : "#fef2f2",
            color: "#b91c1c",
          }}
        >
          {err}
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(280px, 380px)",
          gap: 20,
          alignItems: "start",
        }}
        className="comparar-idealista-grid"
      >
        <style>{`
          @media (max-width: 960px) {
            .comparar-idealista-grid { grid-template-columns: 1fr !important; }
          }
        `}</style>

        {/* Columna datos CRM */}
        <div>
          {data && (
            <>
              <div style={card}>
                <h2 style={{ fontSize: 16, margin: "0 0 12px", color: tx }}>Resumen canónico</h2>
                <div style={{ display: "grid", gap: 8, fontSize: 14, color: tx }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                      gap: 10,
                      fontSize: 13,
                      padding: 10,
                      borderRadius: 8,
                      background: isDark ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.04)",
                    }}
                  >
                    <div>
                      <span style={{ color: t.textMuted }}>Fecha alta (snapshot → fecha_alta)</span>
                      <div style={{ fontWeight: 600 }}>{fmtValor(snap.fecha_alta)}</div>
                    </div>
                    <div>
                      <span style={{ color: t.textMuted }}>Publicación en portal (BD)</span>
                      <div style={{ fontWeight: 600 }}>
                        {fmtFechaHora(data.fecha_publicacion_portal || data.fecha_publicacion_portal_iso)}
                      </div>
                      {data.fecha_publicacion_portal_iso && (
                        <div style={{ fontSize: 11, color: t.textMuted }}>ISO: {data.fecha_publicacion_portal_iso}</div>
                      )}
                    </div>
                    <div>
                      <span style={{ color: t.textMuted }}>Primera importación CRM</span>
                      <div style={{ fontWeight: 600 }}>{fmtFechaHora(data.fecha_primera_importacion_crm)}</div>
                    </div>
                    <div>
                      <span style={{ color: t.textMuted }}>Última importación CRM</span>
                      <div style={{ fontWeight: 600 }}>{fmtFechaHora(data.fecha_ultima_importacion_crm)}</div>
                    </div>
                  </div>
                  <div>
                    <span style={{ color: t.textMuted }}>Título / web_titulo</span>
                    <div style={{ fontWeight: 600 }}>{snap.titulo || snap.web_titulo || "—"}</div>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
                    <div>
                      <span style={{ color: t.textMuted }}>Operación</span>
                      <div>{fmtValor(snap.operacion)}</div>
                    </div>
                    <div>
                      <span style={{ color: t.textMuted }}>
                        Referencia (<code>referencia</code>)
                      </span>
                      <div style={{ fontWeight: 600 }}>{fmtValor(snapReferencia)}</div>
                      {!snapReferencia && (
                        <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4, maxWidth: 420 }}>
                          Si falta: reimporta tras desplegar; buscamos en características, JSON y texto tipo
                          «Referencia: …» / «Ref.: …» en la descripción.
                        </div>
                      )}
                    </div>
                    {(snap.tipo_inmueble_nombre || snap.tipo_inmueble_id != null) && (
                      <div>
                        <span style={{ color: t.textMuted }}>Tipo de propiedad</span>
                        <div>
                          {fmtValor(snap.tipo_inmueble_nombre)}
                          {snap.tipo_inmueble_id != null ? (
                            <span style={{ fontSize: 12, color: t.textMuted }}>
                              {" "}
                              (tipo_inmueble_id: {String(snap.tipo_inmueble_id)})
                            </span>
                          ) : null}
                        </div>
                      </div>
                    )}
                    {(snap.poblacion_nombre ||
                      snap.poblacion_id != null ||
                      extras.poblacion_texto_sin_id) && (
                      <div>
                        <span style={{ color: t.textMuted }}>Población</span>
                        <div>
                          {snap.poblacion_nombre || extras.poblacion_texto_sin_id
                            ? fmtValor(snap.poblacion_nombre || extras.poblacion_texto_sin_id)
                            : "—"}
                          {snap.poblacion_id != null ? (
                            <span style={{ fontSize: 12, color: t.textMuted }}>
                              {" "}
                              (poblacion_id: {String(snap.poblacion_id)})
                            </span>
                          ) : extras.poblacion_texto_sin_id ? (
                            <span style={{ fontSize: 12, color: t.textMuted }}> (sin match en aux_poblacion)</span>
                          ) : null}
                        </div>
                      </div>
                    )}
                    {(snap.orientacion_nombre || snap.orientacion_id != null) && (
                      <div>
                        <span style={{ color: t.textMuted }}>Orientación</span>
                        <div style={{ fontWeight: 600 }}>
                          {snap.orientacion_nombre
                            ? fmtValor(snap.orientacion_nombre)
                            : snap.orientacion_id != null
                              ? "— (sin match en aux_orientacion)"
                              : "—"}
                        </div>
                      </div>
                    )}
                    <div>
                      <span style={{ color: t.textMuted }}>Tipo anunciante</span>
                      <div style={{ fontWeight: 700 }}>{fmtValor(snap.tipo_anunciante)}</div>
                    </div>
                    <div>
                      <span style={{ color: t.textMuted }}>Precio venta (precio_venta)</span>
                      <div style={{ fontWeight: 700 }}>{fmtPrecio(snap.precio_venta)}</div>
                      {data.precio_euros_raw_enrich != null &&
                        (snap.precio_venta == null || snap.precio_venta === "") && (
                        <div style={{ fontSize: 11, color: t.textMuted }}>
                          precioEuros parser: {fmtPrecio(data.precio_euros_raw_enrich)}
                        </div>
                      )}
                    </div>
                    <div>
                      <span style={{ color: t.textMuted }}>Jardín / m² jardín</span>
                      <div>
                        {extras.jardin === false
                          ? "No (ficha portal)"
                          : extras.jardin === true ||
                              snap.m2_jardin != null ||
                              snap.m2_jardin_obs
                            ? [
                                extras.jardin === true || snap.m2_jardin != null || snap.m2_jardin_obs ? "Sí" : null,
                                snap.m2_jardin != null ? `${snap.m2_jardin} m²` : null,
                                snap.m2_jardin_obs || null,
                              ]
                                .filter(Boolean)
                                .join(" · ") || "—"
                            : "— (sin jardín en características ni texto importado)"}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: t.textMuted }}>Teléfono (normalizado)</span>
                      <div style={{ fontWeight: 600 }}>
                        {telefonoResuelto
                          ? fmtValor(telefonoResuelto)
                          : extras.telefono_anuncio
                            ? `${fmtValor(extras.telefono_anuncio)} (raw portal, sin 9 dígitos)`
                            : "—"}
                      </div>
                      {!telefonoResuelto && extras.telefono_aviso && (
                        <div style={{ fontSize: 12, color: "#b45309", marginTop: 4, maxWidth: 520 }}>
                          {extras.telefono_aviso}
                        </div>
                      )}
                      {!telefonoResuelto && extensionTel === "buscando" && (
                        <div style={{ fontSize: 12, color: t.textMuted, marginTop: 4 }}>
                          Obteniendo teléfono vía extensión Chrome (pestaña en segundo plano)…
                        </div>
                      )}
                      {!telefonoResuelto && extensionTel === "sin_extension" && (
                        <div style={{ fontSize: 12, color: "#b45309", marginTop: 4, maxWidth: 520 }}>
                          Instala la extensión Idealista Phone Bridge en Chrome (Configuración → Scraping) con sesión abierta en Idealista.
                        </div>
                      )}
                      {!telefonoResuelto && extensionTel === "rate_limit" && (
                        <div style={{ fontSize: 12, color: "#b45309", marginTop: 4 }}>
                          Espera ~45 s entre anuncios (Idealista limita peticiones). Vuelve a importar.
                        </div>
                      )}
                      {!telefonoResuelto && idAnuncioActual && extensionTel !== "buscando" && (
                        <button
                          type="button"
                          onClick={() => pedirTelefonoExtension(idAnuncioActual, data?.url_origen || url)}
                          style={{
                            marginTop: 6,
                            padding: "4px 10px",
                            fontSize: 11,
                            borderRadius: 6,
                            border: `1px solid ${t.border}`,
                            background: t.bgPanel,
                            color: tx,
                            cursor: "pointer",
                          }}
                        >
                          Reintentar teléfono (extensión)
                        </button>
                      )}
                      {!telefonoResuelto && idAnuncioActual && verTelManual && (
                        <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", maxWidth: 420 }}>
                          <input
                            type="tel"
                            value={telefonoManual}
                            onChange={(e) => setTelefonoManual(e.target.value)}
                            placeholder="Pega teléfono visto en Idealista"
                            disabled={!modTelManual}
                            title={!modTelManual ? "No tienes permiso para introducir el teléfono manual" : undefined}
                            style={{
                              flex: "1 1 160px",
                              minWidth: 140,
                              padding: "6px 10px",
                              fontSize: 13,
                              borderRadius: 6,
                              border: `1px solid ${t.border}`,
                              background: t.bgPanel,
                              color: tx,
                            }}
                          />
                          <button
                            type="button"
                            disabled={guardandoTelefono || !telefonoManual.trim() || !modTelManual}
                            title={!modTelManual ? "No tienes permiso para introducir el teléfono manual" : undefined}
                            onClick={guardarTelefonoManual}
                            style={{
                              padding: "6px 12px",
                              fontSize: 12,
                              fontWeight: 600,
                              borderRadius: 6,
                              border: "none",
                              cursor: guardandoTelefono || !modTelManual ? "not-allowed" : "pointer",
                              background: t.accentOrange || "#ea580c",
                              color: "#fff",
                              opacity: guardandoTelefono || !telefonoManual.trim() || !modTelManual ? 0.55 : 1,
                            }}
                          >
                            {guardandoTelefono ? "Guardando…" : "Guardar teléfono"}
                          </button>
                        </div>
                      )}
                    </div>
                    {(snap.eq_barbacoa != null ||
                      snap.eq_lavadero != null ||
                      caraPortal.barbacoa === true ||
                      caraPortal.lavadero === true) && (
                      <div>
                        <span style={{ color: t.textMuted }}>Barbacoa / lavadero</span>
                        <div>
                          CRM: barbacoa {fmtValor(snap.eq_barbacoa)} · lavadero {fmtValor(snap.eq_lavadero)}
                          {(caraPortal.barbacoa === true || caraPortal.lavadero === true) && (
                            <span style={{ fontSize: 12, color: t.textMuted }}>
                              {" "}
                              · raw portal: barbacoa {String(!!caraPortal.barbacoa)} · lavadero{" "}
                              {String(!!caraPortal.lavadero)}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {snap.ub_vistas_id != null && (
                      <div>
                        <span style={{ color: t.textMuted }}>Vistas (ub_vistas_id)</span>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>
                          {extras.ub_vistas_nombre
                            ? fmtValor(extras.ub_vistas_nombre)
                            : String(snap.ub_vistas_id)}
                        </div>
                      </div>
                    )}
                    <div>
                      <span style={{ color: t.textMuted }}>Dorm. / baños</span>
                      <div>
                        {fmtValor(snap.num_dormitorios)} / {fmtValor(snap.num_banos)}
                      </div>
                    </div>
                    {(snap.altura_planta != null && snap.altura_planta !== "") ||
                    snap.piso ? (
                      <div>
                        <span style={{ color: t.textMuted }}>Planta en edificio (altura_planta / piso)</span>
                        <div>
                          {fmtValor(snap.altura_planta)}
                          {snap.piso ? ` · ${snap.piso}` : ""}
                        </div>
                      </div>
                    ) : null}
                    {snap.eq_ascensor === true || snap.eq_ascensor === false ? (
                      <div>
                        <span style={{ color: t.textMuted }}>Ascensor (eq_ascensor)</span>
                        <div>{snap.eq_ascensor ? "Sí" : "No"}</div>
                      </div>
                    ) : null}
                    <div>
                      <span style={{ color: t.textMuted }}>m² út. / const. / parcela</span>
                      <div>
                        {fmtValor(snap.m2_utiles)} / {fmtValor(snap.m2_construidos)} /{" "}
                        {fmtValor(snap.m2_parcela)}
                      </div>
                    </div>
                  </div>
                  <div>
                    <span style={{ color: t.textMuted }}>Contacto (alias CRM)</span>
                    <div style={{ fontWeight: 600 }}>{fmtValor(snap.alias)}</div>
                    {extras.agente_nombre_portal && (
                      <div style={{ fontSize: 13, marginTop: 4 }}>
                        <span style={{ color: t.textMuted }}>Agente (chat / comercial)</span>
                        <div>{fmtValor(extras.agente_nombre_portal)}</div>
                      </div>
                    )}
                    {extras.agencia_nombre_portal && extras.agencia_nombre_portal !== snap.alias && (
                      <div style={{ fontSize: 13, marginTop: 4 }}>
                        <span style={{ color: t.textMuted }}>Agencia</span>
                        <div>{fmtValor(extras.agencia_nombre_portal)}</div>
                      </div>
                    )}
                    {(extras.contacto_nombre || extras.contacto_apellidos) && (
                      <div style={{ fontSize: 13, marginTop: 4 }}>
                        <span style={{ color: t.textMuted }}>Nombre / apellidos (heurístico)</span>
                        <div>
                          {fmtValor(extras.contacto_nombre)} {fmtValor(extras.contacto_apellidos)}
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <span style={{ color: t.textMuted }}>Dirección (portal)</span>
                    <div style={{ fontSize: 13 }}>
                      <strong>Completa:</strong> {extras.direccion_completa_portal || "—"}
                    </div>
                    <div style={{ marginTop: 4 }}>
                      <strong>Canónico:</strong> {fmtValor(snap.nombre_via)}
                      {snap.numero ? `, nº ${snap.numero}` : ""}
                    </div>
                    <div style={{ marginTop: 4 }}>
                      <strong>CP:</strong> {fmtValor(snap.codigo_postal)}
                    </div>
                    {extras.ubicacion_aprox_portal && (
                      <div style={{ marginTop: 6, fontSize: 12, color: t.textMuted }}>
                        Ubicación aprox. (municipio/zona, no se usa como <code>nombre_via</code>):{" "}
                        {extras.ubicacion_aprox_portal}
                      </div>
                    )}
                  </div>
                  <div>
                    <span style={{ color: t.textMuted }}>
                      Descripción (web_desc_larga_es) — {descLen.toLocaleString("es-ES")} caracteres
                    </span>
                    <div
                      style={{
                        maxHeight: "min(75vh, 900px)",
                        overflow: "auto",
                        lineHeight: 1.45,
                        fontSize: 13,
                        padding: 10,
                        borderRadius: 8,
                        background: isDark ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.04)",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {snap.web_desc_larga_es || "—"}
                    </div>
                  </div>
                </div>
              </div>

              <div style={card}>
                <h2 style={{ fontSize: 16, margin: "0 0 12px", color: tx }}>
                  Snapshot JSON → columnas <code>propiedades</code>
                </h2>
                <p style={{ fontSize: 12, color: t.textMuted, margin: "0 0 10px" }}>
                  Origen de cada valor (trazabilidad): columna derecha pequeña.
                </p>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ textAlign: "left", color: t.textMuted, borderBottom: `1px solid ${t.border}` }}>
                        <th style={{ padding: "8px 6px" }}>Campo CRM</th>
                        <th style={{ padding: "8px 6px" }}>Valor</th>
                        <th style={{ padding: "8px 6px", width: "28%" }}>Fuente / extracción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {snapKeys.map((k) => (
                        <tr key={k} style={{ borderBottom: `1px solid ${t.border}` }}>
                          <td style={{ padding: "8px 6px", fontFamily: "monospace", color: t.accentBlue }}>
                            {k}
                          </td>
                          <td style={{ padding: "8px 6px", color: tx, wordBreak: "break-word" }}>
                            {k === "ub_vistas_id" && extras.ub_vistas_nombre ? (
                              <div style={{ fontWeight: 600 }}>{fmtValor(extras.ub_vistas_nombre)}</div>
                            ) : k === "orientacion_id" && snap.orientacion_nombre ? (
                              <div style={{ fontWeight: 600 }}>{fmtValor(snap.orientacion_nombre)}</div>
                            ) : (
                              fmtValorSnapshotCampo(k, snap, extras, snapReferencia, telefonoResuelto)
                            )}
                          </td>
                          <td style={{ padding: "8px 6px", fontSize: 11, color: t.textMuted }}>
                            {provFuente(k) || "—"}
                          </td>
                        </tr>
                      ))}
                      {IDEALISTA_PORTAL_EXTRAS_ROWS.map((k) => (
                        <tr key={`extra:${k}`} style={{ borderBottom: `1px solid ${t.border}` }}>
                          <td style={{ padding: "8px 6px", fontFamily: "monospace", color: t.accentBlue }}>
                            portal_extras.{k}
                          </td>
                          <td style={{ padding: "8px 6px", color: tx, wordBreak: "break-word" }}>
                            {fmtValor(extras[k])}
                          </td>
                          <td style={{ padding: "8px 6px", fontSize: 11, color: t.textMuted }}>
                            idealista.caracteristicas / multimedia
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <details style={card}>
                <summary style={{ cursor: "pointer", fontWeight: 600, color: tx }}>
                  Características crudas del portal (JSON)
                </summary>
                <pre
                  style={{
                    marginTop: 12,
                    fontSize: 11,
                    overflow: "auto",
                    maxHeight: 280,
                    color: tx,
                    background: isDark ? "rgba(0,0,0,0.25)" : "#f8fafc",
                    padding: 12,
                    borderRadius: 8,
                  }}
                >
                  {JSON.stringify(caraPortal, null, 2)}
                </pre>
              </details>

              <details style={card}>
                <summary style={{ cursor: "pointer", fontWeight: 600, color: tx }}>
                  Extras portal (contacto, teléfono, etc.)
                </summary>
                <pre
                  style={{
                    marginTop: 12,
                    fontSize: 11,
                    overflow: "auto",
                    maxHeight: 220,
                    color: tx,
                    background: isDark ? "rgba(0,0,0,0.25)" : "#f8fafc",
                    padding: 12,
                    borderRadius: 8,
                  }}
                >
                  {JSON.stringify(
                    { ...extras, caracteristicas_portal: "(arriba)" },
                    null,
                    2
                  )}
                </pre>
              </details>

              {videos.length > 0 && (
                <div style={card}>
                  <h2 style={{ fontSize: 16, margin: "0 0 12px", color: tx }}>
                    Vídeos / tour 3D ({videos.length})
                  </h2>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                      gap: 10,
                    }}
                  >
                    {videos.map((src, i) => {
                      const thumb = youtubeThumb(src);
                      return (
                        <a
                          key={i}
                          href={src}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: "block",
                            borderRadius: 8,
                            overflow: "hidden",
                            border: `1px solid ${t.border}`,
                            textDecoration: "none",
                            color: tx,
                          }}
                        >
                          {thumb ? (
                            <img
                              src={thumb}
                              alt=""
                              style={{ width: "100%", height: 112, objectFit: "cover", display: "block" }}
                              loading="lazy"
                            />
                          ) : (
                            <div
                              style={{
                                height: 112,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: t.surfaceMuted || "#f4f4f5",
                                fontSize: 13,
                                padding: 8,
                                textAlign: "center",
                              }}
                            >
                              Abrir vídeo
                            </div>
                          )}
                          <div
                            style={{
                              fontSize: 11,
                              padding: "6px 8px",
                              wordBreak: "break-all",
                              color: t.textMuted,
                            }}
                          >
                            {src.replace(/^https?:\/\//, "")}
                          </div>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              {planosRaw.length > 0 && (
                <div style={card}>
                  <h2 style={{ fontSize: 16, margin: "0 0 12px", color: tx }}>
                    Planos ({planosRaw.length})
                  </h2>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                      gap: 10,
                    }}
                  >
                    {planosRaw.map((src, i) => (
                      <a
                        key={i}
                        href={src}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: "block", borderRadius: 8, overflow: "hidden", border: `1px solid ${t.border}` }}
                      >
                        <img
                          src={proxyFoto(src)}
                          alt=""
                          style={{ width: "100%", height: 120, objectFit: "contain", display: "block", background: "#fff" }}
                          loading="lazy"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {fotos.length > 0 && (
                <div style={card}>
                  <h2 style={{ fontSize: 16, margin: "0 0 12px", color: tx }}>
                    Fotos ({fotos.length})
                  </h2>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                      gap: 10,
                    }}
                  >
                    {fotos.slice(0, 24).map((src, i) => (
                      <a
                        key={i}
                        href={src}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: "block", borderRadius: 8, overflow: "hidden", border: `1px solid ${t.border}` }}
                      >
                        <img
                          src={proxyFoto(src)}
                          alt=""
                          style={{ width: "100%", height: 100, objectFit: "cover", display: "block" }}
                          loading="lazy"
                        />
                      </a>
                    ))}
                  </div>
                  {fotos.length > 24 && (
                    <p style={{ fontSize: 12, color: t.textMuted, margin: "10px 0 0" }}>
                      Mostrando 24 de {fotos.length}; el resto sigue en la respuesta API.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Columna anuncio original */}
        <div style={{ position: "sticky", top: 16 }}>
          <div style={card}>
            <h2 style={{ fontSize: 16, margin: "0 0 10px", color: tx }}>Anuncio original</h2>
            <p style={{ fontSize: 13, color: t.textMuted, lineHeight: 1.5, margin: "0 0 14px" }}>
              Coloca esta página a la izquierda y la ventana del portal a la derecha, o usa dos monitores.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button
                type="button"
                onClick={abrirOriginalNuevaPestaña}
                style={{
                  padding: "12px 16px",
                  borderRadius: 8,
                  border: "none",
                  fontWeight: 700,
                  cursor: "pointer",
                  background: "#ea580c",
                  color: "#fff",
                }}
              >
                Abrir Idealista en nueva pestaña
              </button>
              <button
                type="button"
                onClick={abrirOriginalPopup}
                style={{
                  padding: "12px 16px",
                  borderRadius: 8,
                  border: `1px solid ${t.border}`,
                  fontWeight: 600,
                  cursor: "pointer",
                  background: "transparent",
                  color: tx,
                }}
              >
                Abrir en ventana flotante
              </button>
            </div>
            {(data?.url_origen || url) && (
              <p
                style={{
                  fontSize: 11,
                  wordBreak: "break-all",
                  marginTop: 14,
                  color: t.textMuted,
                  fontFamily: "monospace",
                }}
              >
                {data?.url_origen || url}
              </p>
            )}
          </div>

          {data && (
            <div style={{ ...card, fontSize: 12, color: t.textMuted }}>
              <div>
                <strong style={{ color: tx }}>ID import:</strong> {data.id}
              </div>
              <div style={{ marginTop: 6 }}>
                <strong style={{ color: tx }}>id anuncio:</strong> {data.idealista_ad_id ?? "—"}
              </div>
              <div style={{ marginTop: 6 }}>
                <strong style={{ color: tx }}>referencia:</strong> {fmtValor(snapReferencia)}
              </div>
              <div style={{ marginTop: 6 }}>
                <strong style={{ color: tx }}>teléfono:</strong> {fmtValor(telefonoResuelto)}
              </div>
              <div style={{ marginTop: 6 }}>
                <strong style={{ color: tx }}>Actualizado:</strong>{" "}
                {data.updated_at ? new Date(data.updated_at).toLocaleString("es-ES") : "—"}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
